import { create, StoreApi } from "zustand"
import { subMonths, subWeeks } from "date-fns"
import { OptionsBundle, FileMetadata } from "./appReportsStore"
import { Platform } from "../types/Platforms"
import { fetchOrderHistoryByRestaurant, fetchOrderHistoryForAllRestaurants, fetchOrderHistoryByPlatformIds, fetchOrderHistoryDateBounds } from "../services/orderhistory/orderhistoryService"
import type { PlatformSectionContext } from "./appRestaurantStore"
import { ALL_RESTAURANTS_ID } from "./appUserDefaultStore"
import { platformsWithPerKgPrice } from "../utils/platformUtils"
import { BackendUser } from "../services/user/userService"

// Progressive loading: the first fetch covers only the currently-viewed period
// (defaulting to the last week, matching the default time selector) for the fastest
// possible first render. Everything older then streams in month-by-month in the
// background. When the user switches to an older/larger period, that exact window is
// fetched in one shot ahead of the background crawl (see desiredFloor / the pump below).
const DEFAULT_INITIAL_WINDOW_WEEKS = 1;
const CHUNK_MONTHS = 1;
// Hard cap on the span any single pump request may cover. The desiredFloor gap fetch is
// otherwise unbounded (ChartsPage sets desiredFloor to the beginning of time, asking for
// the client's entire remaining history in one request) — huge payloads are the most
// likely trigger of a 500 from getByPlatformIds, so we slice the gap into cap-sized
// requests walking backwards instead.
const MAX_FETCH_WINDOW_MONTHS = 6;
// Delay before retrying a failed background-pump chunk fetch once (network blip,
// laptop sleep, token refresh) before giving up on that pump run.
const PUMP_RETRY_DELAY_MS = 2000;

// Module-level coordination so a non-progressive caller (e.g. another page) can
// piggy-back on an in-flight progressive load instead of refetching, and so a
// platform switch can cancel an in-flight background loop.
let activeLoadKey: string | null = null;
let activeLoadPromise: Promise<void> | null = null;
let loadGeneration = 0;
// The inputs needed to RESUME a background pump that died or never finished (see
// resumeOrderPump). Captured when a progressive load starts; cleared when the store is
// cleared for a restaurant.
let orderResumeContext: { platformIds: string[]; platformToSectionIndex?: Record<string, PlatformSectionContext> } | null = null;

const platformKey = (platformIds: string[]) => [...platformIds].sort().join(',');

export interface EmailProductData {
    articleId: string;
    quantity: number;
    rawQuantity: number;
    name: string;
    price: number;
    category?: string;
    mainCategory?: string;
    subCategories?: string[];
    quantityInfo?: QuantityInfo;
    // Pre-override quantityInfo; present (incl. null) iff a user override was applied.
    originalQuantityInfo?: QuantityInfo | null;
    priceWithTaxes?: number;
    taxRate?: number;
}

export interface QuantityInfo {
  weightString: string;
  stdAmount: number;
  stdMultiplier: number;
  stdUnit: Unit;
}

export enum Unit {
  GRAM = "g",
  MILLILITER = "ml",
  PIECE = "p",
}


export type TaxCalculationMethod = 'provided' | 'estimated_category_based' | 'estimated_default';

export interface OrderHistory {
    id: string;
    restaurantPlatformId: string;
    messageId: string;
    gmailId: string;
    platform: Platform;
    clientName: string;
    orderNumber: string;
    orderDate: Date;
    total: number;
    totalWithTaxes?: number;
    discount?: number;
    shipping?: number;
    productData: EmailProductData[];
    restaurantSection?: PlatformSectionContext;
    restaurantId?: string;
    restaurantName?: string;
    taxRate?: number;
    taxCalculationMethod?: TaxCalculationMethod;
    unknownSupplierName?: string;
    sourceFileUrl?: string;
    // Linked factuur's PDF URL (set server-side when a factuur is linked to this order).
    factuurSourceFileUrl?: string;
}

export interface OrderHistoryState {
  orderHistoryList: OrderHistory[];
  currentRestaurantId: string | null;
  currentPlatformIds: string[] | null;
  isLoading: boolean;
  // Progressive-loading state (used by the dashboard for early render).
  // firstChunkLoaded: the initial date window has rendered.
  // fullyLoaded: every order back to the earliest order_date is now in the store.
  // loadedFromDate: the earliest date currently covered (null = from the beginning of time).
  // isBackgroundLoading: older chunks are still streaming in.
  firstChunkLoaded: boolean;
  fullyLoaded: boolean;
  loadedFromDate: Date | null;
  isBackgroundLoading: boolean;
  // The oldest date the user currently wants visible. The background pump fetches the
  // gap down to this date in a single request (ahead of its month-by-month crawl) so the
  // selected period shows as fast as possible.
  desiredFloor: Date | null;
  load: (restaurantId: string, platformToSectionIndex?: Record<string, PlatformSectionContext>, backendUser?: BackendUser | null) => Promise<void>
  loadByPlatformIds: (restaurantId: string, platformIds: string[], platformToSectionIndex?: Record<string, PlatformSectionContext>, backendUser?: BackendUser | null) => Promise<void>
  loadByPlatformIdsProgressive: (restaurantId: string, platformIds: string[], platformToSectionIndex?: Record<string, PlatformSectionContext>, backendUser?: BackendUser | null, viewStart?: Date) => Promise<void>
  // Called when the viewed period changes: prioritises fetching back to rangeStart.
  ensureViewLoaded: (rangeStart: Date) => void
  enrichOrdersWithIndex: (platformToSectionIndex: Record<string, PlatformSectionContext>) => void;
  byId: (id: string) => OrderHistory | undefined;
  isDataLoadedFor: (restaurantId: string) => boolean;
  isDataLoadedForPlatforms: (platformIds: string[]) => boolean;
  // True when the given date range is fully covered by loaded data (used to show
  // a loading icon when the user navigates to an older, not-yet-loaded period).
  isRangeLoaded: (rangeStart: Date) => boolean;

  clearOrderhistoryDataForRestaurant: (restaurantId: string) => void;
  markOrderhistoryEmptyForRestaurant: (restaurantId: string) => void;
  removeOrder: (orderId: string) => void;

  userStoredDefaults: Record<string, any>;
  setUserStoredDefaults: (defaults: Record<string, any>) => void;

  orderMetadata: FileMetadata;
  setOrderMetadata: (metadata: FileMetadata) => void;

  optionsBundle: Record<string, OptionsBundle>;
  setOptionsBundle: (bundle: Record<string, OptionsBundle>) => void;
  appendSearchOptionsToBundle: (articleId: string, newOpts: any[]) => void;
  selectedOptionIdMap: Record<string, any>;
  setSelectedOptionIdMap: (sel: Record<string, any>) => void;
  setSelectedPlatforms: (plats: Platform[]) => void;
  setDropdownOpen: (open: boolean) => void;
}

/**
 * Adjusts the price of a product to be per quantityInfo unit when quantity != rawQuantity.
 * This handles cases where the price is per kg/L but should be per quantityInfo amount.
 * Uses quantity/rawQuantity directly to avoid precision loss from integer-rounded stdAmount.
 */
export function adjustProductPriceForQuantityInfo(product: EmailProductData, platform: string): EmailProductData {
  if (!product.quantityInfo ||
      (Number.isInteger(product.quantity) && !platformsWithPerKgPrice.includes(platform)) ||
      Math.abs(product.quantity - product.rawQuantity) < 0.0001 || // quantity == rawQuantity
      !product.quantityInfo.stdAmount ||
      !product.quantityInfo.stdMultiplier ||
      !product.rawQuantity) {
    return product; // No adjustment needed
  }

  // Use quantity / rawQuantity directly for precise factor.
  // Avoids precision loss from integer-rounded stdAmount (e.g. 1073 vs 1073.33).
  const adjustmentFactor = product.quantity / product.rawQuantity;

  return {
    ...product,
    price: product.price * adjustmentFactor,
    priceWithTaxes: product.priceWithTaxes
      ? product.priceWithTaxes * adjustmentFactor
      : undefined
  };
}

/**
 * Maps each order's restaurant section (with fallback) and adjusts product prices
 * for quantityInfo. Shared by every load path so chunked/progressive loads enrich
 * exactly like full loads.
 */
export function enrichOrders(
  data: OrderHistory[],
  platformToSectionIndex?: Record<string, PlatformSectionContext>
): OrderHistory[] {
  return data.map(o => {
    const key = String(o.restaurantPlatformId).trim();
    let section = platformToSectionIndex?.[key];
    if (!section && platformToSectionIndex) {
      // Legacy fallback: some rows carry a SECTION id in restaurant_platform_id. Values are keyed by
      // platform but spread from the section, so `.id` is the section id. Deliberately not widened —
      // the entry it lands on is an arbitrary platform of that section, so its
      // `combinedOrderingAccount` is a guess. Matching orders are the same ones that were already
      // resolved this way before the flag existed.
      section = Object.values(platformToSectionIndex).find(s => String(s.id) === key);
    }
    const adjustedProductData = o.productData.map(p => adjustProductPriceForQuantityInfo(p, o.platform));
    return { ...o, restaurantSection: section, productData: adjustedProductData };
  });
}

/** Merge new orders into an existing list, de-duplicating by id (last write wins). */
function mergeOrdersDedup(existing: OrderHistory[], incoming: OrderHistory[]): OrderHistory[] {
  const map = new Map(existing.map(o => [o.id, o]));
  for (const o of incoming) map.set(o.id, o);
  return Array.from(map.values());
}

type OrderSet = StoreApi<OrderHistoryState>['setState'];
type OrderGet = StoreApi<OrderHistoryState>['getState'];

/**
 * Background pump: crawl older orders from the current loadedFromDate down to minDate
 * (the earliest order_date), fetching the gap to desiredFloor in one shot when the user
 * has selected a further-back period, otherwise one month at a time. Marks the store
 * fullyLoaded when it reaches minDate. Shared by the initial progressive load and the
 * resume path so a pump that died mid-crawl can restart without refetching loaded data.
 * A failed chunk fetch is retried once after a short delay before giving up; on giving
 * up the store stays at !fullyLoaded with its loaded list intact so ensureViewLoaded can
 * resume it. Honours the generation/stale mechanism via isStale.
 */
async function pumpOlderOrders(
  set: OrderSet,
  get: OrderGet,
  platformIds: string[],
  platformToSectionIndex: Record<string, PlatformSectionContext> | undefined,
  minDate: Date,
  isStale: () => boolean,
): Promise<void> {
  while (true) {
    if (isStale()) return;
    const cursorEnd = get().loadedFromDate;
    if (!cursorEnd || cursorEnd <= minDate) break;

    const desired = get().desiredFloor;
    let cursorStart: Date;
    if (desired && desired < cursorEnd) {
      // Fetch toward the desired floor, but never span more than MAX_FETCH_WINDOW_MONTHS
      // in one request — when the floor is further back than the cap, take a cap-sized
      // slice (nearest first) and let the loop walk the rest, so a far-back floor never
      // becomes a single huge request.
      const cappedStart = subMonths(cursorEnd, MAX_FETCH_WINDOW_MONTHS);
      const target = desired < cappedStart ? cappedStart : desired;
      cursorStart = target < minDate ? minDate : target;
    } else {
      const candidateStart = subMonths(cursorEnd, CHUNK_MONTHS);
      cursorStart = candidateStart < minDate ? minDate : candidateStart;
    }

    let chunk: OrderHistory[];
    try {
      chunk = await fetchOrderHistoryByPlatformIds(platformIds, cursorStart, cursorEnd);
    } catch {
      // Retry once after a short delay before giving up, so a transient failure doesn't
      // strand the store at !fullyLoaded forever (ensureViewLoaded can resume later).
      if (isStale()) return;
      await new Promise(r => setTimeout(r, PUMP_RETRY_DELAY_MS));
      if (isStale()) return;
      try {
        chunk = await fetchOrderHistoryByPlatformIds(platformIds, cursorStart, cursorEnd);
      } catch (retryErr) {
        console.error('[ERROR] progressive order history pump chunk failed after retry', retryErr);
        if (!isStale()) set({ isBackgroundLoading: false });
        return;
      }
    }
    if (isStale()) return;
    set(s => ({
      orderHistoryList: mergeOrdersDedup(s.orderHistoryList, enrichOrders(chunk, platformToSectionIndex)),
      loadedFromDate: cursorStart,
    }));
  }

  if (isStale()) return;
  set({ fullyLoaded: true, isBackgroundLoading: false, loadedFromDate: minDate });
}

/**
 * Resume a background pump that stopped before loading the full history (it gave up after
 * a failed fetch, or its process was interrupted). Re-derives the date bounds and restarts
 * the crawl from the current loadedFromDate WITHOUT clearing the loaded list. No-op if no
 * resume context was captured. Called by ensureViewLoaded when there's loaded data but not
 * the full history and no load is currently running.
 */
function resumeOrderPump(set: OrderSet, get: OrderGet): void {
  const ctx = orderResumeContext;
  if (!ctx) return;
  const key = platformKey(ctx.platformIds);
  const myGeneration = ++loadGeneration;
  const isStale = () => loadGeneration !== myGeneration;
  activeLoadKey = key;

  const run = (async () => {
    set({ isBackgroundLoading: true });
    try {
      const { minDate } = await fetchOrderHistoryDateBounds(ctx.platformIds).catch(() => ({ minDate: null, maxDate: null }));
      if (isStale()) return;
      // Can't determine the floor — stay resumable rather than looping unbounded.
      if (!minDate) { set({ isBackgroundLoading: false }); return; }
      // pumpOlderOrders handles the already-covered case (breaks immediately → fullyLoaded).
      await pumpOlderOrders(set, get, ctx.platformIds, ctx.platformToSectionIndex, minDate, isStale);
    } catch (err) {
      console.error('[ERROR] resuming progressive order history pump', err);
      if (!isStale()) set({ isBackgroundLoading: false });
    } finally {
      if (activeLoadKey === key) { activeLoadKey = null; activeLoadPromise = null; }
    }
  })();
  activeLoadPromise = run;
}

export const useOrderHistory = create<OrderHistoryState>((set, get) => ({
  orderHistoryList: [],
  currentRestaurantId: null,
  currentPlatformIds: null,
  isLoading: false,
  firstChunkLoaded: false,
  fullyLoaded: false,
  loadedFromDate: null,
  isBackgroundLoading: false,
  desiredFloor: null,
  optionsBundle: {},
  selectedOptionIdMap: {},
  orderMetadata: {
    fileName: '',
    startDate: '',
    endDate: '',
  },

  userStoredDefaults: {
    rangeType: 'week',
    currentStart: new Date(),
    selectedPlatforms: Object.values(Platform).filter((v): v is Platform => typeof v === 'string') as Platform[],
    dropdownOpen: false,
  },
  setUserStoredDefaults: defaults =>
    set(state => ({
      userStoredDefaults: { ...state.userStoredDefaults, ...defaults }
  })),

  setSelectedPlatforms: plats =>
    set(state => ({
      userStoredDefaults: { ...state.userStoredDefaults, selectedPlatforms: plats }
  })),

  setDropdownOpen: open =>
    set(state => ({
      userStoredDefaults: { ...state.userStoredDefaults, dropdownOpen: open }
  })),

  load: async (restaurantId: string, platformToSectionIndex?: Record<string, PlatformSectionContext>, backendUser?: BackendUser | null) => {
    // Skip if data is already loaded for this restaurant
    if (get().isDataLoadedFor(restaurantId)) {
      return;
    }

    set({ isLoading: true });

    try {
      let data: OrderHistory[];

      if (restaurantId === ALL_RESTAURANTS_ID) {
        data = await fetchOrderHistoryForAllRestaurants();
      } else {
        data = await fetchOrderHistoryByRestaurant(restaurantId);
      }

      const enriched = enrichOrders(data, platformToSectionIndex);

      set({
        orderHistoryList: enriched,
        currentRestaurantId: restaurantId,
        currentPlatformIds: null,
        isLoading: false,
        firstChunkLoaded: true,
        fullyLoaded: true,
        loadedFromDate: null,
        isBackgroundLoading: false,
      });
    } catch (err) {
      console.error('[ERROR] loading order history', err);
      set({ isLoading: false });
    }
  },

  loadByPlatformIds: async (restaurantId: string, platformIds: string[], platformToSectionIndex?: Record<string, PlatformSectionContext>, backendUser?: BackendUser | null) => {
    // Skip if data is already fully loaded for these platform IDs
    if (get().isDataLoadedForPlatforms(platformIds)) {
      return;
    }

    // If a progressive (or other) load for these exact platforms is already in
    // flight, piggy-back on it instead of firing a second full fetch.
    const key = platformKey(platformIds);
    if (activeLoadKey === key && activeLoadPromise) {
      set({ isLoading: true });
      try {
        await activeLoadPromise;
      } catch { /* errors already logged by the in-flight load */ }
      // The awaited progressive/full load signals completion via fullyLoaded, not
      // isLoading, so our `isLoading: true` above would otherwise stick forever. That
      // includes the pump-give-up case (run resolves with fullyLoaded still false), so
      // key off "no load is running anymore" rather than fullyLoaded — if a newer load
      // superseded the awaited one (e.g. a restaurant switch), activeLoadPromise is
      // non-null and that load manages its own isLoading.
      if (activeLoadPromise === null) set({ isLoading: false });
      return;
    }

    const myGeneration = ++loadGeneration;
    activeLoadKey = key;
    set({ isLoading: true });

    const run = (async () => {
      try {
        const data = await fetchOrderHistoryByPlatformIds(platformIds);
        if (loadGeneration !== myGeneration) return; // superseded by a newer load

        const enriched = enrichOrders(data, platformToSectionIndex);

        set({
          orderHistoryList: enriched,
          currentPlatformIds: platformIds,
          currentRestaurantId: restaurantId,
          isLoading: false,
          firstChunkLoaded: true,
          fullyLoaded: true,
          loadedFromDate: null,
          isBackgroundLoading: false,
        });
      } catch (err) {
        console.error('[ERROR] loading order history by platform IDs', err);
        set({ isLoading: false });
      } finally {
        if (activeLoadKey === key) { activeLoadKey = null; activeLoadPromise = null; }
      }
    })();
    activeLoadPromise = run;
    await run;
  },

  loadByPlatformIdsProgressive: async (restaurantId: string, platformIds: string[], platformToSectionIndex?: Record<string, PlatformSectionContext>, backendUser?: BackendUser | null, viewStart?: Date) => {
    // Skip if already fully loaded for these platforms.
    if (get().isDataLoadedForPlatforms(platformIds)) {
      return;
    }

    const key = platformKey(platformIds);
    // A load for these platforms is already running — the dashboard reads store
    // state reactively, so there's nothing to await; just don't start a second one.
    if (activeLoadKey === key && activeLoadPromise) {
      return;
    }

    const myGeneration = ++loadGeneration;
    const isStale = () => loadGeneration !== myGeneration;
    activeLoadKey = key;
    // Capture what a later resume needs to restart the pump (see resumeOrderPump).
    orderResumeContext = { platformIds, platformToSectionIndex };

    const now = new Date();
    // The initial fetch covers the currently-viewed period, extended one week further
    // back so a freshly-started week (whose window is nearly empty) still shows the
    // previous week without waiting for a follow-up fetch. Falls back to the default
    // window when no viewStart is given.
    const initialStart = viewStart ? subWeeks(viewStart, 1) : subWeeks(now, DEFAULT_INITIAL_WINDOW_WEEKS);

    const run = (async () => {
      set({
        isLoading: true,
        isBackgroundLoading: true,
        firstChunkLoaded: false,
        fullyLoaded: false,
        loadedFromDate: null,
        desiredFloor: initialStart,
        orderHistoryList: [],
        currentPlatformIds: platformIds,
        currentRestaurantId: restaurantId,
      });

      try {
        // Fetch the earliest order_date in parallel so we know how far to chunk.
        const boundsPromise = fetchOrderHistoryDateBounds(platformIds).catch(() => ({ minDate: null, maxDate: null }));

        // 1) Initial window — render as soon as this arrives.
        const initialData = await fetchOrderHistoryByPlatformIds(platformIds, initialStart, now);
        if (isStale()) return;
        set({
          orderHistoryList: enrichOrders(initialData, platformToSectionIndex),
          loadedFromDate: initialStart,
          firstChunkLoaded: true,
          isLoading: false,
        });

        const { minDate } = await boundsPromise;
        if (isStale()) return;

        // Nothing older than the initial window — we're done.
        if (!minDate || minDate >= initialStart) {
          set({ fullyLoaded: true, isBackgroundLoading: false, loadedFromDate: minDate ?? initialStart });
          return;
        }

        // 2) Background pump: keep loading older orders until the earliest order_date
        // (marks fullyLoaded on completion). See pumpOlderOrders for the crawl/gap logic.
        await pumpOlderOrders(set, get, platformIds, platformToSectionIndex, minDate, isStale);
      } catch (err) {
        console.error('[ERROR] progressive order history load', err);
        if (!isStale()) set({ isLoading: false, isBackgroundLoading: false });
      } finally {
        if (activeLoadKey === key) { activeLoadKey = null; activeLoadPromise = null; }
      }
    })();
    activeLoadPromise = run;
    await run;
  },

  ensureViewLoaded: (rangeStart: Date) => {
    const state = get();
    // Everything is already in the store, or the requested period is already covered.
    if (state.fullyLoaded) return;
    if (state.loadedFromDate && rangeStart >= state.loadedFromDate) return;
    // Only ever push the floor older — the running background pump picks this up and
    // fetches the gap in one request before resuming its month-by-month crawl.
    if (!state.desiredFloor || rangeStart < state.desiredFloor) {
      set({ desiredFloor: rangeStart });
    }
    // If the initial window has landed but the pump is no longer running (it died after
    // a failed fetch, or was interrupted), restart it — otherwise desiredFloor would be
    // set with nothing reading it and this period would spin forever.
    if (state.firstChunkLoaded && activeLoadPromise === null) {
      resumeOrderPump(set, get);
    }
  },

  enrichOrdersWithIndex: (platformToSectionIndex) => {
    set(state => ({
      orderHistoryList: state.orderHistoryList.map(o => {
        const key = String(o.restaurantPlatformId).trim();
        let section = platformToSectionIndex[key];
        return { ...o, restaurantSection: section };
      })
    }));
  },

  byId: id => get().orderHistoryList.find(o => o.id === id),

  setOrderMetadata: metadata => set({ orderMetadata: metadata }),

  setOptionsBundle: bundle => set({ optionsBundle: bundle }),

  appendSearchOptionsToBundle: (articleId, newOpts) =>
    set(state => {
      const existing = state.optionsBundle[articleId] ?? { sourcingOptions: [], searchOptions: [] };
      const merged = [...existing.searchOptions, ...newOpts];
      const dedup = Array.from(new Map(merged.map(p => [p.articleId, p])).values());
      return {
        optionsBundle: {
          ...state.optionsBundle,
          [articleId]: {
            ...existing,
            searchOptions: dedup,
          },
        },
      };
    }),

  setSelectedOptionIdMap: sel => set({ selectedOptionIdMap: sel }),

  isDataLoadedFor: (restaurantId: string) => get().currentRestaurantId === restaurantId,

  isDataLoadedForPlatforms: (platformIds: string[]) => {
    const state = get();
    // Only treat as "loaded" once the full history is present, so non-progressive
    // consumers (charts, reports, cost price) never read a partial dataset.
    if (!state.fullyLoaded) return false;
    const current = state.currentPlatformIds;
    if (!current || current.length !== platformIds.length) return false;
    const sortedCurrent = [...current].sort();
    const sortedNew = [...platformIds].sort();
    return sortedCurrent.every((id, index) => id === sortedNew[index]);
  },

  isRangeLoaded: (rangeStart: Date) => {
    const state = get();
    if (state.fullyLoaded) return true;
    if (!state.firstChunkLoaded || !state.loadedFromDate) return false;
    // The requested period is covered iff it starts at/after the earliest loaded date.
    return rangeStart >= state.loadedFromDate;
  },

  clearOrderhistoryDataForRestaurant: (restaurantId: string) => {
    loadGeneration++; // cancel any in-flight background load
    activeLoadKey = null;
    activeLoadPromise = null;
    orderResumeContext = null; // don't resume a pump for the cleared restaurant
    set({
      orderHistoryList: [],
      currentPlatformIds: [],
      currentRestaurantId: null,
      firstChunkLoaded: false,
      fullyLoaded: false,
      loadedFromDate: null,
      isBackgroundLoading: false,
      desiredFloor: null,
    });
  },

  // Restaurant has no platforms configured: nothing to fetch, so mark the store as
  // fully loaded with an empty list. Without this, consumers that gate on
  // firstChunkLoaded/fullyLoaded (e.g. the dashboard) would spin forever instead of
  // showing 0.
  markOrderhistoryEmptyForRestaurant: (restaurantId: string) => {
    loadGeneration++; // cancel any in-flight background load
    activeLoadKey = null;
    activeLoadPromise = null;
    orderResumeContext = null; // don't resume a pump for the cleared restaurant
    set({
      orderHistoryList: [],
      currentPlatformIds: [],
      currentRestaurantId: restaurantId,
      isLoading: false,
      firstChunkLoaded: true,
      fullyLoaded: true,
      loadedFromDate: null,
      isBackgroundLoading: false,
      desiredFloor: null,
    });
  },

  removeOrder: (orderId: string) => {
    set(state => ({
      orderHistoryList: state.orderHistoryList.filter(o => o.id !== orderId)
    }));
  }
}));